/**
 * Pure decision logic for the microphone level meter.
 *
 * Free of React and of the Web Audio API so it can be unit tested without
 * mocking browser media APIs. Time is always injected, never read from a clock.
 *
 * Background: Chrome does not set MediaStreamTrack.muted when the microphone is
 * muted at the OS level. It hands us a live track full of digital silence, so
 * measuring the signal is the only reliable way to tell a user their mic is dead.
 *
 * We measure the raw waveform (getFloatTimeDomainData), NOT the spectrum. Time
 * domain samples are plain amplitudes in [-1, 1] with an unambiguous scale, they
 * are not affected by the analyser's smoothing, and a muted device produces
 * literal zeros. Frequency-domain dB values are per-FFT-bin and sit on a scale
 * that depends on window and FFT size, which makes any absolute threshold there
 * a guess.
 */

// Standard peak-meter range in dBFS. 0 dBFS is full scale.
export const METER_MIN_DBFS = -60;
export const METER_MAX_DBFS = 0;
export const METER_SEGMENTS = 12;

// The silence threshold IS the meter's own display floor (-60 dBFS): if the
// bar has shown nothing for the grace period, the caption explains why.
//
// History (dev testing 2026-07-30): an earlier version used a near-digital-zero
// threshold (1e-5, below one 16-bit converter step) on the theory that only a
// dead device should ever warn. Chrome's default noise suppression gates a
// live-but-idle mic into the band between that and the display floor - a
// completely flat bar with no warning, which users read as "no audio and no
// explanation". One threshold, tied to what the bar can display, keeps the
// meter and its caption telling the same story. OS-muted mics emit exact
// zeros, so they sit far below this floor and still warn.
export const QUIET_AMPLITUDE = Math.pow(10, METER_MIN_DBFS / 20); // 1e-3

// How long the bar must stay flat before we say anything. Before recording we
// can be prompt. During recording we are deliberately slower: the warning is
// passive, and a thinking pause or a soft-spoken stretch must not trigger a
// nag mid-pitch - being late is better. Q&A practice is the worst case (users
// think in silence between questions); the 10s grace covers normal pauses and
// the caption is one small line that clears the moment they speak.
export const PRE_RECORDING_WARNING_MS = 3000;
export const RECORDING_WARNING_MS = 10000;

/**
 * Largest absolute sample in a window of raw waveform data.
 *
 * @param {Float32Array|number[]} samples - getFloatTimeDomainData output, [-1, 1]
 * @returns {number} peak amplitude, 0 for empty or missing input
 */
export function peakAmplitude(samples) {
    if (!samples || !samples.length) {
        return 0;
    }

    let peak = 0;
    for (let i = 0; i < samples.length; i++) {
        const value = samples[i];
        if (isNaN(value)) {
            continue;
        }
        const magnitude = value < 0 ? -value : value;
        if (magnitude > peak) {
            peak = magnitude;
        }
    }
    return peak;
}

/**
 * Convert a peak amplitude to dBFS. Silence maps to -Infinity.
 * @param {number} amplitude
 */
export function amplitudeToDbfs(amplitude) {
    if (typeof amplitude !== 'number' || isNaN(amplitude) || amplitude <= 0) {
        return -Infinity;
    }
    return 20 * Math.log10(amplitude);
}

/**
 * True when the amplitude is below the display floor (or a caller-supplied
 * threshold): nothing the meter could show is arriving.
 * @param {number} amplitude - peak amplitude for the sample window
 * @param {number} threshold
 */
export function isSilentAmplitude(amplitude, threshold = QUIET_AMPLITUDE) {
    if (typeof amplitude !== 'number' || isNaN(amplitude)) {
        return true;
    }
    return amplitude < threshold;
}

/**
 * Map a dBFS level onto a whole number of lit meter segments.
 *
 * Note this quantizes but does not add hysteresis, so a level hovering exactly
 * on a step boundary will flip between two counts. That is normal meter
 * behaviour and is cheap because the meter owns its own subtree.
 *
 * @param {number} dbfs
 * @returns {number} integer in 0..METER_SEGMENTS
 */
export function levelToSegments(dbfs) {
    if (typeof dbfs !== 'number' || isNaN(dbfs) || dbfs <= METER_MIN_DBFS) {
        return 0;
    }
    if (dbfs >= METER_MAX_DBFS) {
        return METER_SEGMENTS;
    }

    const ratio = (dbfs - METER_MIN_DBFS) / (METER_MAX_DBFS - METER_MIN_DBFS);
    return Math.round(ratio * METER_SEGMENTS);
}

/** Starting tracker. Frozen: it is handed out as a return value. */
export const EMPTY_SILENCE_TRACKER = Object.freeze({ silentSince: null });

/**
 * Advance the silence run-length tracker with a new sample.
 *
 * The returned object identity is stable for as long as a single silent run
 * continues. That is only an allocation optimisation: the run's DURATION keeps
 * growing every tick, so callers must never skip work merely because the
 * identity is unchanged.
 *
 * @param {{silentSince: number|null}} tracker
 * @param {number} amplitude - peak amplitude for this sample window
 * @param {number} now - monotonic ms, injected
 * @param {number} threshold - amplitude floor this tracker times runs under
 * @returns {{silentSince: number|null}}
 */
export function nextSilenceTracker(tracker, amplitude, now, threshold = QUIET_AMPLITUDE) {
    const current = tracker || EMPTY_SILENCE_TRACKER;

    if (isSilentAmplitude(amplitude, threshold)) {
        // Already timing this run: keep the original start time.
        if (current.silentSince !== null) {
            return current;
        }
        return { silentSince: now };
    }

    if (current.silentSince === null) {
        return current;
    }
    return EMPTY_SILENCE_TRACKER;
}

/**
 * How long the current silent run has lasted, 0 when not silent.
 * @param {{silentSince: number|null}} tracker
 * @param {number} now - monotonic ms, injected
 */
export function silenceDurationMs(tracker, now) {
    if (!tracker || tracker.silentSince === null) {
        return 0;
    }
    return Math.max(0, now - tracker.silentSince);
}

/**
 * The single display decision for the meter.
 *
 * Precedence: hidden > muted > warning > normal.
 *
 * `muted` is decided from isMicrophoneActive and NEVER from the measured level.
 * The meter analyses a clone of the mic stream, and MediaStreamTrack.clone()
 * snapshots `enabled` at clone time, so later presses of the app's Mute button
 * do not reach the clone. While in-app muted the meter still reads live audio,
 * and only the flag tells the truth.
 *
 * One condition, two delays: the warning fires when the bar has displayed
 * nothing (level below QUIET_AMPLITUDE) for the grace period - 3s before
 * recording, 10s during. See the constants above for why the delays differ.
 *
 * @param {Object} params
 * @param {boolean} params.available - meter has a working analyser
 * @param {boolean} params.isMicrophoneActive - false when muted via the app's Mute button
 * @param {boolean} params.isRecording
 * @param {number} params.quietMs - ms the level has been below QUIET_AMPLITUDE
 * @returns {'hidden'|'muted'|'warning'|'normal'}
 */
export function meterDisplayState({ available, isMicrophoneActive, isRecording, quietMs }) {
    if (!available) {
        return 'hidden';
    }
    if (!isMicrophoneActive) {
        return 'muted';
    }

    const threshold = isRecording ? RECORDING_WARNING_MS : PRE_RECORDING_WARNING_MS;
    if ((quietMs || 0) >= threshold) {
        return 'warning';
    }

    return 'normal';
}
